import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-create-faq-dialog',
  templateUrl: './create-faq-dialog.component.html',
  styleUrls: ['./create-faq-dialog.component.scss']
})
export class CreateFAQDialogComponent implements OnInit {
  readonly faqForm: FormGroup = this.fb.group({
    linkedTenderId: ['', Validators.required],
    question: ['', Validators.required],
    answer: ['', Validators.required]
  });

  isEditMode = false;
  faqId: number | null = null;
  tenders: any[] = [];
  tenderIds: string[] = [];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CreateFAQDialogComponent>,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private toasterService: ToasterService,
    @Inject(MAT_DIALOG_DATA) public data?: { faq: any }
  ) {
    if (data?.faq) {
      this.isEditMode = true;
      this.faqId = data.faq.id || data.faq.ID;
      this.faqForm.patchValue({
        linkedTenderId: data.faq.linkedTenderId || data.faq.LinkedTenderId || '',
        question: data.faq.question || data.faq.Question || '',
        answer: data.faq.answer || data.faq.Answer || ''
      });
    }
  }

  async ngOnInit(): Promise<void> {
    // Only load tenders if not in edit mode (not needed for edit mode)
    if (!this.isEditMode) {
      await this.loadTenders();
    }
  }

  private async loadTenders(): Promise<void> {
    try {
      this._loaderService.showLoader();
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.TENDERS}/${AppConstants.ACTIONS.GetTenderListData}?scope=2`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));

      this._loaderService.hideLoader();

      if (response && (response.IsSuccess || response.isSuccess) && response.data) {
        this.tenders = Array.isArray(response.data) ? response.data : [];
        // Extract TenderIDs for searchable-select component
        this.tenderIds = this.tenders
          .map(tender => this.getTenderId(tender))
          .filter(id => id && id.trim() !== '');
      } else {
        this.tenders = [];
        this.tenderIds = [];
        console.warn('[Create FAQ] Failed to load tenders:', response);
      }
    } catch (error: any) {
      this._loaderService.hideLoader();
      console.error('[Create FAQ] Error loading tenders:', error);
      this.tenders = [];
      this.tenderIds = [];
      this.toasterService.showError('Failed to load tenders list', 'Error');
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  private async createFAQ(): Promise<void> {
    try {
      const payload = {
        linkedTenderId: this.faqForm.get('linkedTenderId')?.value?.trim() || '',
        question: this.faqForm.get('question')?.value?.trim() || '',
        answer: this.faqForm.get('answer')?.value?.trim() || '',
      };

      this._loaderService.showLoader();

      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.FAQs}/${AppConstants.ACTIONS.CreateFAQ}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, payload));

      this._loaderService.hideLoader();

      if (response && (response.IsSuccess || response.isSuccess || response.data)) {
        this.toasterService.showSuccess('FAQ created successfully', 'Success');
        this.dialogRef.close(true);
      } else {
        const errorMessage = response?.ErrorMessageEn || response?.errorMessageEn || response?.message || 'Failed to create FAQ';
        this.toasterService.showError(errorMessage, 'Error');
      }
    } catch (error: any) {
      this._loaderService.hideLoader();
      const errorMessage = error?.error?.ErrorMessageEn || 
                          error?.error?.errorMessageEn || 
                          error?.error?.message || 
                          error?.message || 
                          'An error occurred while creating the FAQ';
      this.toasterService.showError(errorMessage, 'Error');
      console.error('[Create FAQ] Error:', error);
    }
  }

  private async updateFAQ(): Promise<void> {
    if (!this.faqId) {
      this.toasterService.showError('FAQ ID is missing', 'Error');
      return;
    }

    try {
      const payload = {
        linkedTenderId: this.faqForm.get('linkedTenderId')?.value?.trim() || '',
        question: this.faqForm.get('question')?.value?.trim() || '',
        answer: this.faqForm.get('answer')?.value?.trim() || '',
      };

      this._loaderService.showLoader();

      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.FAQs}/${AppConstants.ACTIONS.UpdateFAQ}/${this.faqId}`;
      const response: any = await lastValueFrom(this.apiService.putByUrl(url, payload));

      this._loaderService.hideLoader();

      if (response && (response.IsSuccess || response.isSuccess || response.data)) {
        this.toasterService.showSuccess('FAQ updated successfully', 'Success');
        this.dialogRef.close(true);
      } else {
        const errorMessage = response?.ErrorMessageEn || response?.errorMessageEn || response?.message || 'Failed to update FAQ';
        this.toasterService.showError(errorMessage, 'Error');
      }
    } catch (error: any) {
      this._loaderService.hideLoader();
      const errorMessage = error?.error?.ErrorMessageEn || 
                          error?.error?.errorMessageEn || 
                          error?.error?.message || 
                          error?.message || 
                          'An error occurred while updating the FAQ';
      this.toasterService.showError(errorMessage, 'Error');
      console.error('[Update FAQ] Error:', error);
    }
  }

  async onSubmit(action: 'save' | 'create'): Promise<void> {
    if (this.faqForm.valid) {
      if (this.isEditMode) {
        await this.updateFAQ();
      } else {
        await this.createFAQ();
      }
    } else {
      this.markFormGroupTouched();
      this.toasterService.showError('Please fill in all required fields', 'Validation Error');
    }
  }

  onSave(): void {
    this.onSubmit('save');
  }

  onCreateFAQ(): void {
    this.onSubmit('create');
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.faqForm.get(fieldName);
    return !!(control?.invalid && control?.touched);
  }

  getFieldControl(fieldName: string): AbstractControl | null {
    return this.faqForm.get(fieldName);
  }

  private markFormGroupTouched(): void {
    Object.keys(this.faqForm.controls).forEach(key => {
      this.faqForm.get(key)?.markAsTouched();
    });
  }

  getTenderId(tender: any): string {
    return tender.tenderID || tender.tenderId || tender.TenderID || '';
  }

  getTenderDisplayName(tender: any): string {
    return tender.tenderID || tender.tenderId || tender.TenderID || 'Unknown Tender';
  }
}
